export const CORNER_ANGLE_EDIT_HISTORY_STORAGE_KEY =
  'nodon.design.corner-angle-edit-history';

type CornerAngleEditHistory = Record<string, number>;
type CornerAngleStorage = Pick<Storage, 'getItem' | 'setItem'>;

export type CornerAngleEditIdentity = {
  storeyId: string;
  shapeId: string;
  pointCount: number;
  pointIndex: number;
};

const getDefaultStorage = (): CornerAngleStorage | undefined =>
  typeof window === 'undefined' ? undefined : window.sessionStorage;

const getCornerKey = (identity: CornerAngleEditIdentity): string =>
  JSON.stringify([
    identity.storeyId,
    identity.shapeId,
    identity.pointCount,
    identity.pointIndex,
  ]);

const readHistory = (
  storage: CornerAngleStorage | undefined,
): CornerAngleEditHistory => {
  if (!storage) return {};
  try {
    const parsed: unknown = JSON.parse(
      storage.getItem(CORNER_ANGLE_EDIT_HISTORY_STORAGE_KEY) ?? '{}',
    );
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, number] =>
          typeof entry[1] === 'number' && Number.isFinite(entry[1]),
      ),
    );
  } catch {
    return {};
  }
};

export const getManualCornerAngleTimestamps = (
  identity: Omit<CornerAngleEditIdentity, 'pointIndex'>,
  storage: CornerAngleStorage | undefined = getDefaultStorage(),
): ReadonlyMap<number, number> => {
  const history = readHistory(storage);
  const timestamps = new Map<number, number>();
  for (let pointIndex = 0; pointIndex < identity.pointCount; pointIndex += 1) {
    const timestamp = history[getCornerKey({ ...identity, pointIndex })];
    if (timestamp !== undefined) timestamps.set(pointIndex, timestamp);
  }
  return timestamps;
};

export const recordManualCornerAngleEdit = (
  identity: CornerAngleEditIdentity,
  storage: CornerAngleStorage | undefined = getDefaultStorage(),
  now = Date.now(),
): number | undefined => {
  if (!storage) return undefined;
  const history = readHistory(storage);
  const latestTimestamp = Object.values(history).reduce(
    (latest, timestamp) => Math.max(latest, timestamp),
    0,
  );
  const timestamp = Math.max(now, latestTimestamp + 1);
  history[getCornerKey(identity)] = timestamp;
  try {
    storage.setItem(
      CORNER_ANGLE_EDIT_HISTORY_STORAGE_KEY,
      JSON.stringify(history),
    );
    return timestamp;
  } catch {
    return undefined;
  }
};
